import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';
import { STATES, getDistricts } from '../../data/indiaLocations';

const EMPTY_FORM = {
  mobile: '', full_name: '', guardian_name: '', gender: 'male', dob: '',
  city_village: '', address_line: '', pincode: '', education_level: 'illiterate',
  experience_years: '', daily_wage_min: '', daily_wage_max: '',
  travel_radius_km: '', mobile_alt: '', availability_status: 'available',
  verification_status: 'unverified',
  emergency_contact_name: '', emergency_contact_mobile: '',
};

const SEL_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none';

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 font-medium">{value || '—'}</p>
    </div>
  );
}

export default function AdminWorkers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workers, setWorkers]           = useState([]);
  const [pagination, setPagination]     = useState({});
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');

  const [detailWorker, setDetailWorker]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editForm, setEditForm]         = useState(null);
  const [editLoading, setEditLoading]   = useState(false);
  const [editState, setEditState]       = useState('');
  const [editDistrict, setEditDistrict] = useState('');

  const [addModal, setAddModal]         = useState(false);
  const [addForm, setAddForm]           = useState(EMPTY_FORM);
  const [addLoading, setAddLoading]     = useState(false);
  const [addState, setAddState]         = useState('');
  const [addDistrict, setAddDistrict]   = useState('');

  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [verifyTarget, setVerifyTarget]   = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);

  const verificationFilter = searchParams.get('verification_status') || '';

  async function loadWorkers(page = 1) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (verificationFilter) params.set('verification_status', verificationFilter);
      if (search) params.set('q', search);
      const res = await api.get(`/admin/workers?${params}`);
      setWorkers(res.data.data);
      setPagination(res.data.pagination);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadWorkers(); }, [verificationFilter, search]);

  async function openDetail(w) {
    setDetailLoading(true);
    setDetailWorker({ ...w, _loading: true });
    try {
      const res = await api.get(`/admin/workers/${w.id}`);
      setDetailWorker(res.data.data);
    } finally { setDetailLoading(false); }
  }

  async function openEdit(w) {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/workers/${w.id}`);
      const p = res.data.data;
      setEditForm({
        _id: p.id,
        _name: p.full_name,
        full_name: p.full_name || '',
        guardian_name: p.guardian_name || '',
        gender: p.gender || 'male',
        dob: p.dob ? p.dob.split('T')[0] : '',
        address_line: p.address_line || '',
        pincode: p.pincode || '',
        education_level: p.education_level || 'illiterate',
        experience_years: p.experience_years ?? '',
        daily_wage_min: p.daily_wage_min || '',
        daily_wage_max: p.daily_wage_max || '',
        travel_radius_km: p.travel_radius_km ?? '',
        mobile_alt: p.mobile_alt || '',
        availability_status: p.availability_status || 'available',
        verification_status: p.verification_status || 'unverified',
        emergency_contact_name: p.emergency_contact_name || '',
        emergency_contact_mobile: p.emergency_contact_mobile || '',
        city_village: p.city_village || '',
      });
      setEditState(p.state || '');
      setEditDistrict(p.district || '');
    } finally { setDetailLoading(false); }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!editState || !editDistrict) { toast.error('Please select State and District'); return; }
    setEditLoading(true);
    try {
      const locRes = await api.post('/lookup/location-resolve', {
        state: editState, district: editDistrict,
        city_village: editForm.city_village || null,
        pincode: editForm.pincode || null,
      });
      const location_id = locRes.data.data.id;
      const { _id, _name, city_village, ...body } = editForm;
      await api.put(`/admin/workers/${_id}`, { ...body, location_id, city_village });
      toast.success('Worker updated');
      setEditForm(null);
      loadWorkers();
    } finally { setEditLoading(false); }
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!addState || !addDistrict) { toast.error('Please select State and District'); return; }
    setAddLoading(true);
    try {
      const locRes = await api.post('/lookup/location-resolve', {
        state: addState, district: addDistrict,
        city_village: addForm.city_village || null,
        pincode: addForm.pincode || null,
      });
      const location_id = locRes.data.data.id;
      await api.post('/admin/workers', { ...addForm, location_id });
      toast.success('Worker created');
      setAddModal(false);
      setAddForm(EMPTY_FORM);
      setAddState('');
      setAddDistrict('');
      loadWorkers();
    } finally { setAddLoading(false); }
  }

  async function handleDelete() {
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/workers/${deleteTarget.id}`);
      toast.success('Worker deleted');
      setDeleteTarget(null);
      loadWorkers();
    } finally { setDeleteLoading(false); }
  }

  async function handleVerify(workerId, action, reason = '') {
    setRejectLoading(true);
    try {
      await api.post(`/admin/workers/${workerId}/verify`, { action, reason });
      toast.success(`Worker ${action === 'approve' ? 'verified' : 'rejected'}`);
      setVerifyTarget(null);
      loadWorkers();
    } finally { setRejectLoading(false); }
  }

  const curState    = editForm ? editState    : addState;
  const setCurState = editForm
    ? (v) => { setEditState(v); setEditDistrict(''); }
    : (v) => { setAddState(v); setAddDistrict(''); };
  const curDistrict    = editForm ? editDistrict    : addDistrict;
  const setCurDistrict = editForm ? setEditDistrict : setAddDistrict;

  const F = (label, key, type = 'text', opts = {}) => (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <input type={type} value={editForm ? editForm[key] ?? '' : addForm[key] ?? ''}
        onChange={(e) => editForm ? setEditForm(p => ({ ...p, [key]: e.target.value })) : setAddForm(p => ({ ...p, [key]: e.target.value }))}
        className={SEL_CLS}
        {...opts} />
    </div>
  );

  const S = (label, key, options) => (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <select value={editForm ? editForm[key] ?? '' : addForm[key] ?? ''}
        onChange={(e) => editForm ? setEditForm(p => ({ ...p, [key]: e.target.value })) : setAddForm(p => ({ ...p, [key]: e.target.value }))}
        className={SEL_CLS}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  const WorkerForm = ({ isEdit, onSubmit, loading: submitting }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {!isEdit && F('Mobile Number *', 'mobile', 'tel', { required: true, placeholder: '10-digit mobile' })}
      {F('Full Name *', 'full_name', 'text', { required: true })}
      {F('Father / Husband Name', 'guardian_name')}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {S('Gender', 'gender', [['male','Male'],['female','Female'],['other','Other']])}
        {F('Date of Birth', 'dob', 'date')}
      </div>
      {/* State & District */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-600">State *</label>
        <select value={curState} onChange={(e) => setCurState(e.target.value)} className={SEL_CLS} required>
          <option value="">— Select State / UT —</option>
          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-600">District *</label>
        <select value={curDistrict} onChange={(e) => setCurDistrict(e.target.value)} className={SEL_CLS} required disabled={!curState}>
          <option value="">— Select District —</option>
          {getDistricts(curState).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {!curState && <p className="text-xs text-gray-400">Select state first</p>}
      </div>
      {F('Village / Town / City', 'city_village', 'text', { placeholder: 'Locality name' })}
      {F('Full Address', 'address_line')}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Pincode', 'pincode', 'text', { maxLength: 6 })}
        {S('Education', 'education_level', [
          ['illiterate','Not Educated'],['primary','Primary'],['middle','Middle'],
          ['secondary','10th'],['higher_secondary','12th'],['graduate','Graduate'],
        ])}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Experience (yrs)', 'experience_years', 'number')}
        {F('Travel Radius (km)', 'travel_radius_km', 'number')}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Wage Min (₹)', 'daily_wage_min', 'number')}
        {F('Wage Max (₹)', 'daily_wage_max', 'number')}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Alternate Mobile', 'mobile_alt', 'tel')}
        {S('Availability', 'availability_status', [
          ['available','Available'],['unavailable','Not Available'],['on_job','On Job'],
        ])}
      </div>
      {isEdit && S('Verification Status', 'verification_status', [
        ['unverified','Unverified'],['pending','Pending'],['verified','Verified'],['rejected','Rejected'],
      ])}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Emergency Contact', 'emergency_contact_name')}
        {F('Emergency Mobile', 'emergency_contact_mobile', 'tel')}
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={() => isEdit ? setEditForm(null) : setAddModal(false)}>Cancel</Button>
        <Button type="submit" fullWidth loading={submitting}>{isEdit ? 'Save Changes' : 'Create Worker'}</Button>
      </div>
    </form>
  );

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900">Workers</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-gray-500 hidden sm:inline">{pagination.total} total</span>
            <Button onClick={() => { setAddModal(true); setAddForm(EMPTY_FORM); setAddState(''); setAddDistrict(''); }}>+ Add</Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3">
          <input type="search" placeholder="Search name, mobile, reg. no..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none" />
          <select value={verificationFilter}
            onChange={(e) => { const p = new URLSearchParams(searchParams); p.set('verification_status', e.target.value); setSearchParams(p); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
            <option value="">All Status</option>
            <option value="unverified">Unverified</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Reg. No.', 'Name', 'Mobile', 'District', 'Experience', 'Availability', 'Verification', 'Expiry', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={9} className="py-12 text-center text-gray-400">Loading...</td></tr>
                ) : workers.length === 0 ? (
                  <tr><td colSpan={9} className="py-12 text-center text-gray-400">No workers found</td></tr>
                ) : workers.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{w.registration_number}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{w.full_name}</td>
                    <td className="px-4 py-3 text-gray-600">{w.mobile}</td>
                    <td className="px-4 py-3 text-gray-600">{w.district || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{w.experience_years}y</td>
                    <td className="px-4 py-3"><Badge label={w.availability_status} variant={w.availability_status} /></td>
                    <td className="px-4 py-3"><Badge label={w.verification_status} variant={w.verification_status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {w.expiry_date ? new Date(w.expiry_date).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        <button onClick={() => openDetail(w)}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200">View</button>
                        <button onClick={() => openEdit(w)}
                          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">Edit</button>
                        {w.verification_status === 'pending' && (
                          <button onClick={() => handleVerify(w.id, 'approve')}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">✓</button>
                        )}
                        {(w.verification_status === 'pending' || w.verification_status === 'unverified') && (
                          <button onClick={() => setVerifyTarget(w)}
                            className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200">✗</button>
                        )}
                        <button onClick={() => setDeleteTarget(w)}
                          className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700">Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-500">Page {pagination.page} of {pagination.totalPages}</span>
              <div className="flex gap-2">
                {pagination.hasPrev && <Button size="sm" variant="secondary" onClick={() => loadWorkers(pagination.page - 1)}>← Prev</Button>}
                {pagination.hasNext && <Button size="sm" variant="secondary" onClick={() => loadWorkers(pagination.page + 1)}>Next →</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Detail Modal ── */}
      {detailWorker && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{detailWorker.full_name}</h3>
                <p className="text-xs text-gray-400 font-mono">{detailWorker.registration_number}</p>
              </div>
              <button onClick={() => setDetailWorker(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            {detailLoading && detailWorker._loading ? (
              <div className="p-8 text-center text-gray-400">Loading...</div>
            ) : (
              <div className="p-5 space-y-5 overflow-y-auto max-h-[75vh]">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Personal</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Field label="Mobile" value={detailWorker.mobile} />
                    <Field label="Gender" value={detailWorker.gender} />
                    <Field label="DOB" value={detailWorker.dob ? new Date(detailWorker.dob).toLocaleDateString('en-IN') : null} />
                    <Field label="Guardian" value={detailWorker.guardian_name} />
                    <Field label="Education" value={detailWorker.education_level} />
                    <Field label="Aadhaar Last 4" value={detailWorker.aadhaar_last4} />
                    <Field label="Emergency Contact" value={detailWorker.emergency_contact_name} />
                    <Field label="Emergency Mobile" value={detailWorker.emergency_contact_mobile} />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Address & Work</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Field label="State" value={detailWorker.state} />
                    <Field label="District" value={detailWorker.district} />
                    <Field label="Village / Town" value={detailWorker.city_village} />
                    <Field label="Address" value={detailWorker.address_line} />
                    <Field label="Pincode" value={detailWorker.pincode} />
                    <Field label="Experience" value={detailWorker.experience_years != null ? `${detailWorker.experience_years} yrs` : null} />
                    <Field label="Travel Radius" value={detailWorker.travel_radius_km != null ? `${detailWorker.travel_radius_km} km` : null} />
                    <Field label="Wage Range" value={detailWorker.daily_wage_min ? `₹${detailWorker.daily_wage_min} – ₹${detailWorker.daily_wage_max || '?'}` : null} />
                    <Field label="Alt Mobile" value={detailWorker.mobile_alt} />
                    <Field label="UPI ID" value={detailWorker.upi_id} />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Status</p>
                  <div className="flex gap-4 flex-wrap">
                    <div><p className="text-xs text-gray-400">Availability</p><Badge label={detailWorker.availability_status} variant={detailWorker.availability_status} /></div>
                    <div><p className="text-xs text-gray-400">Verification</p><Badge label={detailWorker.verification_status} variant={detailWorker.verification_status} /></div>
                    <Field label="Expiry" value={detailWorker.expiry_date ? new Date(detailWorker.expiry_date).toLocaleDateString('en-IN') : null} />
                    <Field label="Registered" value={detailWorker.created_at ? new Date(detailWorker.created_at).toLocaleDateString('en-IN') : null} />
                  </div>
                </div>
                {detailWorker.skills?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Skills</p>
                    <div className="flex flex-wrap gap-2">
                      {detailWorker.skills.map((s, i) => (
                        <span key={i} className="bg-brand-50 text-brand-700 text-xs px-2.5 py-1 rounded-full border border-brand-200">
                          {s.skill_name || s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {detailWorker.languages?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Languages</p>
                    <div className="flex flex-wrap gap-2">
                      {detailWorker.languages.map((l, i) => (
                        <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full">
                          {l.language_name || l.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {detailWorker.preferences && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Preferences</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <Field label="Work Type" value={detailWorker.preferences.preferred_work_type} />
                      <Field label="Shift" value={detailWorker.preferences.shift_preference} />
                      <Field label="Open to Relocation" value={detailWorker.preferences.open_to_relocation ? 'Yes' : 'No'} />
                    </div>
                  </div>
                )}
                <div className="flex gap-3 pt-2 border-t border-gray-100">
                  <Button variant="secondary" onClick={() => { setDetailWorker(null); openEdit(detailWorker); }}>Edit Worker</Button>
                  <Button variant="danger" onClick={() => { setDetailWorker(null); setDeleteTarget(detailWorker); }}>Delete Worker</Button>
                  <Button variant="secondary" fullWidth onClick={() => setDetailWorker(null)}>Close</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">Edit — {editForm._name}</h3>
              <button onClick={() => setEditForm(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[80vh]">
              <WorkerForm isEdit onSubmit={handleEdit} loading={editLoading} />
            </div>
          </div>
        </div>
      )}

      {/* ── Add Modal ── */}
      {addModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">Add New Worker</h3>
              <button onClick={() => setAddModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[80vh]">
              <WorkerForm isEdit={false} onSubmit={handleAdd} loading={addLoading} />
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-gray-900 mb-2">Delete Worker</h3>
            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete <strong>{deleteTarget.full_name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="secondary" fullWidth onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="danger" fullWidth loading={deleteLoading} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {verifyTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-gray-900 mb-4">Reject — {verifyTarget.full_name}</h3>
            <textarea id="reject-reason"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 focus:ring-2 focus:ring-brand-500 focus:outline-none"
              placeholder="Reason for rejection (will be sent to worker)..." />
            <div className="flex gap-3 mt-4">
              <Button variant="secondary" onClick={() => setVerifyTarget(null)}>Cancel</Button>
              <Button variant="danger" loading={rejectLoading}
                onClick={() => handleVerify(verifyTarget.id, 'reject', document.getElementById('reject-reason').value)}>
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
